import { ABTestManager } from "./ABTestManager";

/**
 * Funnel entry-page mention redirects.
 *
 * Live campaigns still point at the legacy `weight_loss` funnel entry pages
 * (`/start-consultation/:country/:language/:mention` and
 * `/questionnaire/:country/:language/:category`). Rather than re-tag every
 * campaign, the funnel rewrites those two entry pages to their `_2`
 * counterpart, so a visitor who lands on the old URL starts the new funnel.
 *
 * Only DE is redirected here. The UK splits `weight_loss` between itself and `weight_loss_2`
 * instead (getFunnelSplitRedirectUrl). `weight_loss` is also published for FR, which has no
 * `_2` catalog — redirecting it would 404.
 *
 * The two DE funnels were the arms of the WL Care+ A/B test (HL-10959), allocated
 * on the CMS pages. WL2 won and is rolled out to everyone, so the redirect no
 * longer honours the `wl_new_design_ab_variant=control` cookie the CMS allocator
 * mirrors: a visitor still carrying a stale control cookie (up to a year) must not
 * be the last one on WL1.
 */
export const FUNNEL_MENTION_REDIRECTS: Record<string, Record<string, string>> = {
  de: { weight_loss: "weight_loss_2" },
};

/**
 * HL-11440: mentions that only change which questionnaire is loaded. The cart and
 * every step after the questionnaire (select treatment, plan, shipping, order) use
 * the target mention, whose catalog holds the products.
 */
export const QUESTIONNAIRE_ONLY_MENTIONS: Record<string, string> = { weight_loss_3: "weight_loss_2" };

export const getCartMention = (mention: string | undefined): string | undefined =>
  mention ? (QUESTIONNAIRE_ONLY_MENTIONS[mention.toLowerCase()] ?? mention) : mention;

/** `&cartMention=` for the questionnaire GET of a questionnaire-only mention, so its prefill finds orders on the cart mention. */
export const getCartMentionQuery = (mention: string | undefined): string => {
  const cartMention = getCartMention(mention);
  return cartMention && cartMention !== mention ? `&cartMention=${encodeURIComponent(cartMention)}` : "";
};

/**
 * The questionnaire a funnel mention opens. The WL2 vs WL3 questionnaire test
 * (HL-11440 / HL-12122) is over and the short weight_loss_3 questionnaire won, so
 * DE weight_loss_2 opens weight_loss_3 (the cart still hands off to weight_loss_2,
 * see QUESTIONNAIRE_ONLY_MENTIONS). Start consultation keeps weight_loss_2: only the
 * questionnaire changes. A product-catalog URL (`/questionnaire/de/de/wegovy`)
 * keeps the WL2 questionnaire.
 *
 * In-app links go straight to the target (getQuestionnaireMention). The questionnaire
 * route guard applies the same map, as a fallback for old links (ads, bookmarks, emails).
 */
export const QUESTIONNAIRE_MENTION_REDIRECTS: Record<string, Record<string, string>> = {
  de: { weight_loss_2: "weight_loss_3" },
};

/**
 * The short V3 questionnaire of each country: DE weight_loss_3, UK weight_loss_2 (the UK has no
 * weight_loss_3). `funnel` event property of the WL experiments: wl3 for it, wl2 for every other mention.
 */
const V3_QUESTIONNAIRE_MENTIONS: Record<string, string> = { de: "weight_loss_3", gb: "weight_loss_2" };

export const getWlFunnelVariant = (mention: unknown, country?: string): "wl2" | "wl3" =>
  String(mention ?? "").trim().toLowerCase() === (V3_QUESTIONNAIRE_MENTIONS[(country ?? "").toLowerCase()] ?? "weight_loss_3") ? "wl3" : "wl2";

/** Inverse of getCartMention: the questionnaire mention to link to for a funnel mention. */
export const getQuestionnaireMention = <T extends string | undefined>(country: string | undefined, mention: T): string | T =>
  (country && mention && QUESTIONNAIRE_MENTION_REDIRECTS[country.toLowerCase()]?.[mention.toLowerCase()]) || mention;

export type FunnelEntryPage = "start-consultation" | "questionnaire";

/** `cat` mirrors the mention for tracking/flags, so it travels with the path. */
const CAT_PARAM = "cat";

export const getRedirectedMention = (
  country: string | undefined,
  mention: string | undefined,
  page: FunnelEntryPage = "start-consultation"
): string | null => {
  if (!country || !mention) return null;
  const countryKey = country.toLowerCase();
  const legacyMention = mention.toLowerCase();
  const funnelMention = FUNNEL_MENTION_REDIRECTS[countryKey]?.[legacyMention] ?? legacyMention;
  // Resolved in one step, so the legacy weight_loss questionnaire lands on weight_loss_3 in a single hop.
  const target =
    page === "questionnaire"
      ? QUESTIONNAIRE_MENTION_REDIRECTS[countryKey]?.[funnelMention] ?? funnelMention
      : funnelMention;
  return target === legacyMention ? null : target;
};

/**
 * Swaps the mention segment of `pathname` (the last non-empty segment) for
 * `targetMention`, keeping the rest of the path — and any trailing slash —
 * exactly as the visitor typed it.
 */
const rewritePathname = (pathname: string, targetMention: string): string => {
  const segments = pathname.split("/");
  for (let i = segments.length - 1; i >= 0; i--) {
    if (segments[i] !== "") {
      segments[i] = targetMention;
      break;
    }
  }
  return segments.join("/");
};

/**
 * Rewrites `cat` when it echoes the legacy mention, mirroring what the CMS
 * weight-loss allocator does to the hidden `cat` input. Every other param —
 * utm_*, gclid, coupon, catalog_id — is preserved verbatim: campaign
 * attribution must survive the redirect.
 */
const rewriteSearch = (search: string, legacyMention: string, targetMention: string): string => {
  if (!search) return "";
  const params = new URLSearchParams(search);
  if (params.get(CAT_PARAM)?.toLowerCase() === legacyMention.toLowerCase()) {
    params.set(CAT_PARAM, targetMention);
  }
  const next = params.toString();
  return next ? `?${next}` : "";
};

/** `location` with its funnel mention (path segment and `?cat=`) swapped for `targetMention`. */
export const getMentionSwapUrl = (
  location: { pathname: string; search?: string; hash?: string },
  mention: string,
  targetMention: string
): string =>
  rewritePathname(location.pathname, targetMention) +
  rewriteSearch(location.search ?? "", mention, targetMention) +
  (location.hash ?? "");

/**
 * Full replacement URL for a funnel entry location, or null when the location
 * is not a redirected mention (the page then renders as usual).
 */
export const getFunnelMentionRedirectUrl = (
  country: string | undefined,
  mention: string | undefined,
  location: { pathname: string; search?: string; hash?: string },
  page: FunnelEntryPage = "start-consultation"
): string | null => {
  const targetMention = getRedirectedMention(country, mention, page);
  if (!targetMention || !mention) return null;

  return getMentionSwapUrl(location, mention, targetMention);
};

/**
 * Funnel entry pages whose URL shape is
 * `/{page}/{country}/{language}/{mention}`. Used by callers that only have a
 * location and no route params (e.g. PageTracker, which renders above Routes).
 */
const REDIRECTED_ENTRY_PAGES: FunnelEntryPage[] = ["start-consultation", "questionnaire"];

/**
 * Same as getFunnelMentionRedirectUrl, but derives country and mention from the
 * pathname instead of route params — for code that runs outside a matched route.
 */
export const getFunnelMentionRedirectUrlForLocation = (location: {
  pathname: string;
  search?: string;
  hash?: string;
}): string | null => {
  const segments = location.pathname.split("/").filter((segment) => segment !== "");
  if (segments.length !== 4) return null;

  const [page, country, , mention] = segments;
  if (!REDIRECTED_ENTRY_PAGES.includes(page as FunnelEntryPage)) return null;

  return getFunnelMentionRedirectUrl(country, mention, location, page as FunnelEntryPage);
};

/** UK WL1 vs WL2 split as the start consultation / questionnaire GET returns it (null outside GB weight_loss). */
export type FunnelSplit = { mention: string; weight: number };

/**
 * The URL to send this visitor to when the split puts them on the other funnel, else null.
 * Weight (0..1, `uk_wl2_split_weight`) = share moved: 0 = nobody, 1 = everyone, in between a
 * sticky per-browser roll (localStorage `abtest_uk_wl2_split`), so a visitor keeps their funnel.
 */
export const getFunnelSplitRedirectUrl = (
  split: FunnelSplit | null | undefined,
  mention: string | undefined,
  location: { pathname: string; search?: string; hash?: string }
): string | null => {
  if (!split?.mention || !mention) return null;
  const weight = Math.min(Math.max(Number(split.weight) || 0, 0), 1);
  if (weight <= 0) return null;
  const variant = weight >= 1
    ? "B"
    : new ABTestManager({ testName: "uk_wl2_split", variants: ["A", "B"], weights: [1 - weight, weight] }).getVariant();
  return variant === "B" ? getMentionSwapUrl(location, mention, split.mention) : null;
};
